import React, { memo, useState } from "react";
import { cls } from "app/utils";
import styles from "./styles.module.scss";

// This React version predates useId, so a module counter supplies the unique id
// the label/input association needs. useState's initialiser runs once per mount,
// which keeps the id stable across re-renders.
let instanceCount = 0;

interface IProps {
  label?: string;
  placeholder?: string;
  value?: string;
  readOnly?: boolean;
  maxLength?: number;
  error?: string;
  multiline?: boolean;
  /** Announced to assistive tech; the form is noValidate, so validation stays the step's. */
  required?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
}

const WizardTextField = ({
  label,
  placeholder,
  value,
  readOnly,
  maxLength,
  error,
  multiline,
  required,
  onChange,
  onBlur,
}: IProps) => {
  const [id] = useState(() => `wizard-text-field-${(instanceCount += 1)}`);
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className={styles.container}>
      {label && (
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
      )}
      {multiline ? (
        <textarea
          id={id}
          className={cls(styles.input, styles.textarea, {
            [styles.input_error]: !!error,
          })}
          placeholder={placeholder}
          // Same controlled/uncontrolled split as the inputs below.
          value={onChange ? value ?? "" : undefined}
          defaultValue={onChange ? undefined : value}
          readOnly={readOnly}
          maxLength={maxLength}
          aria-invalid={!!error}
          aria-required={required || undefined}
          aria-describedby={errorId}
          onChange={
            onChange ? (event) => onChange(event.target.value) : undefined
          }
          onBlur={onBlur}
        />
      ) : onChange ? (
        <input
          id={id}
          type="text"
          className={cls(styles.input, { [styles.input_error]: !!error })}
          placeholder={placeholder}
          value={value ?? ""}
          readOnly={readOnly}
          maxLength={maxLength}
          aria-invalid={!!error}
          aria-required={required || undefined}
          aria-describedby={errorId}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
        />
      ) : (
        // Design-only steps pass a static value without a handler; defaultValue
        // keeps them editable without React's controlled-input warning.
        <input
          id={id}
          type="text"
          className={styles.input}
          placeholder={placeholder}
          defaultValue={value}
          readOnly={readOnly}
        />
      )}
      {error && (
        <span id={errorId} className={styles.error}>
          {error}
        </span>
      )}
    </div>
  );
};

export default memo(WizardTextField);
